import {Event} from '../../constants/Event'
import {isDefined, isNotDefined, safeString} from './Functions'

/**
 * Helper class for managing nodes in the specified document
 */
class DocumentNode {

    constructor(event, document, node) {
        this.event = event
        this.document = document
        this.node = node
    }

    /**
     * Gets or creates a node
     * @param element Element to look for
     * @param attributeDefinitions The attributes to apply if the element is created
     * @param selector Optional selector to find the element
     * @return {DocumentNode} An existing, or newly created, node
     */
    getOrCreate({element, attributeDefinitions = {}, selector = undefined}) {
        if (isNotDefined(element)) {
            throw new Error('Missing element key')
        }

        let target = selector ? this.node.querySelector(selector) : this.node.querySelector(element)
        if (!target) {
            target = this.create({element, attributeDefinitions}).node
        }

        return new DocumentNode(this.event, this.document, target)
    }

    /**
     * Create a node
     * @param element the name of the node
     * @param attributeDefinitions The attributes to apply to the element
     * @return {DocumentNode} A newly created node
     */
    create({element, attributeDefinitions = {}}) {
        const target = this.document.createElementNS(this.node.namespaceURI, element)
        this.node.appendChild(target)

        let documentNode = new DocumentNode(this.event, this.document, target)

        documentNode.setAttributes({attributeDefinitions})

        return documentNode
    }

    /**
     * Remove a child element if it exists
     * @param element The name of the element to remove
     * @param selector An optional selector for finding an element,
     * @return {DocumentNode}
     */
    removeIfExists({element, selector = undefined}) {
        let target = selector ? this.node.querySelector(selector) : this.node.querySelector(element)

        if (target && target.parentNode === this.node) {
            this.node.removeChild(target)
        }
        return this
    }

    /**
     * Set an attribute on current node
     * @param attribute Attribute name
     * @param value The value
     * @return {DocumentNode}
     */
    setAttribute(attribute, value) {
        this.node.setAttribute(attribute, safeString(value))
        return this
    }

    getAttribute(attribute) {
        return this.node.getAttribute(attribute)
    }

    setAttributes({attributeDefinitions = {}}) {
        if (attributeDefinitions) {
            Object.entries(attributeDefinitions).forEach(([key, value]) => this.node.setAttribute(key, value))
        }

        return this
    }

    setAttributesFromFields({source, fields}) {
        fields.forEach((field) => {
            if (isDefined(source[field])) {
                this.node.setAttribute(field, source[field])
            }
        })
        return this
    }

    setText(value) {
        this.node.textContent = safeString(value)
        return this
    }

    triggerEvent({name, eventType, action, key, value}) {
        switch(eventType) {
            case Event.DOCUMENT_CHANGED_EXTERNAL:
                this.event.trigger(name, Event.DOCUMENT_CHANGED_EXTERNAL, {action, key, value})
                break
            default:
                const changeData = Object.assign(value || {}, {action})
                this.event.trigger(name, Event.DOCUMENT_CHANGED, changeData)
        }
    }

    /**
     * Returns operations to execute if a node exists.
     * @param selector The selector for a specific node
     * @return {{thenExecute: thenExecute, thenThrow: thenThrow}}
     */
    ifExists(selector) {
        const selectorResult = this.node.querySelector(selector)
        if (selectorResult !== null) {
            return {
                /**
                 * A function for executing on the specific node
                 * @param fn The function that is executed if the node exists. The parameter to the function is the
                 * node found by the selector.
                 */
                thenExecute: (fn) => {
                    fn(new DocumentNode(this.event, this.document, selectorResult))
                },
                /**
                 * A function for throwing an exception
                 * @param message The message to throw if the node exists.
                 */
                thenThrow: (message) => {
                    throw new Error(message)
                }
            }
        } else {
            return {
                /**
                 * Empty function, since the node does not exist
                 */
                thenExecute: () => {
                },
                /**
                 * Empty function, since the node does not exist
                 */
                thenThrow: () => {
                }
            }
        }
    }
}

export {DocumentNode}
